import { Dispatch, SetStateAction } from 'react';

export interface IPatientInformationProps {
	smartyKey: string;
	setModalType: Dispatch<SetStateAction<'existingConsultation' | 'promptToCall'>>;
}

interface AdditionalMarketingParams {
	leadId: string;
	session_id: string;
	domain: string;
	pathname: string;
}

export interface Question {
	id: number;
	short_name: string;
	options: { id: number; short_name: string }[];
}

export const parseAndSaveMarketingData = async (
	marketingSourceCookie: string,
	additionalMarketingParams: AdditionalMarketingParams
) => {
	const { leadId, session_id, domain, pathname } = additionalMarketingParams;
	const urlParams = new URLSearchParams(marketingSourceCookie);
	const marketingData = {
		session_id,
		timestamp: Date.now(),
		domain,
		pathname,
	};
	const paramExclusion = ['fc'];

	for (const entry of urlParams.entries()) {
		const paramName = entry[0];

		if (!paramExclusion.includes(paramName)) {
			if (paramName === 'trans_id') {
				marketingData[paramName] = [entry[1]];
			} else {
				marketingData[paramName] = entry[1];
			}
		}
	}

	await fetch(process.env.APP_PATH + `/api/clear-choice/marketing-data/`, {
		method: 'POST',
		body: JSON.stringify({ leadId, marketingData }),
	});
};

export const mapDentalConditionCodes = (conditions: string[]) => {
	return conditions?.map((conditionString) => {
		switch (conditionString) {
			case 'missing_teeth':
				return '1.1';
			case 'failing_teeth':
				return '1.2';
			case 'periodontal_disease':
				return '1.3';
			case 'none':
				return '1.4';
			default:
				return conditionString;
		}
	});
};

export const mapDentalSolutionCodes = (solutions: string[]) => {
	return solutions?.map((solutionString) => {
		switch (solutionString) {
			case 'denture':
				return '2.1';
			case 'bridge':
				return '2.2';
			case 'implant':
				return '2.3';
			case 'none':
				return '2.4';
			default:
				return solutionString;
		}
	});
};

// Functionality for VWO Experiment Data - triggered on step 2 submission
export const getExperimentData = () => {
	const experiments: any[] = [];

	const referrerPage = document.referrer;
	const conversionPage = window.location.pathname;

	// Retrieve any and all campaign data from the global object
	const campaignData =
		globalThis._vwo_campaignData && typeof globalThis._vwo_campaignData === 'object'
			? globalThis._vwo_campaignData
			: {};

	// Iterate through campaigns in _vwo_campaignData
	for (const experimentID in campaignData) {
		if (campaignData.hasOwnProperty(experimentID)) {
			const experiment = campaignData[experimentID];

			// Retrieve experiment variation directly
			const experimentVariation = experiment && experiment.c !== undefined ? experiment.c.toString() : undefined;

			experiments.push({
				experimentID,
				experimentVariation,
				referrerPage,
				conversionPage,
				timestampFirstTouch: new Date().getTime().toString(),
			});
		}
	}

	// Capture experiment data from cookies (for both split and combi test cookies)
	const matchCookiePattern = /vis_opt_exp_(\d+)_(combi|split)/i;
	const cookies = document.cookie.split(/;\s*/);

	const splitCookies = cookies
		.map((cookie) => cookie.trim())
		.filter((cookie) => matchCookiePattern.test(cookie))
		.map((cookie) => {
			const expData = cookie.match(/\d+/g);
			return expData && expData.length === 2
				? {
						experimentID: expData[0],
						experimentVariation: expData[1],
						referrerPage,
						conversionPage,
						timestampFirstTouch: new Date().getTime().toString(),
				  }
				: null;
		})
		.filter(Boolean); // Remove null entries

	// Merge campaignData and cookieData into single array
	experiments.push(...splitCookies);

	return experiments;
};
